import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { useToken } from "../../../server/auth/use-token";
import { ImagesService } from "../../../server/cloudflare/images-service";
import { uuidv4 } from "../../../utils/uuid";

import type { GetTokenSilentlyOptions } from "@auth0/auth0-react";

export function useUploadImage(): (
  file?: { attachments?: File[] },
  done?: (result: { progress: number; url?: string }) => void,
) => void {
  const getToken = useToken();

  return useCallback(
    (
      file?: { attachments?: File[] },
      done?: (result: { progress: number; url?: string }) => void,
    ) => {
      void saveImage(getToken, file, done);
    },
    [getToken],
  );
}

async function saveImage(
  getToken: (options?: GetTokenSilentlyOptions) => Promise<string>,
  file?: { attachments?: File[] },
  done?: (result: { progress: number; url?: string }) => void,
): Promise<void> {
  // Unlayer waits on done() for every upload, so each failure path must report back to it.
  const fail = (error?: unknown): void => {
    console.error("Image upload failed", error);
    toast.error("Image upload failed. Please try again.");
    done?.({ progress: 0 });
  };

  const uploadFile = file?.attachments?.[0];
  if (!uploadFile) {
    fail(new Error("No file attached"));
    return;
  }

  done?.({ progress: 10 });
  try {
    const name = uuidv4();
    const imagesService = new ImagesService();
    const result = await imagesService.upload(uploadFile, name, await getToken());
    if (!result.success || !result.url) {
      fail(new Error("Upload returned no URL"));
      return;
    }
    done?.({ progress: 100, url: result.url });
  } catch (error) {
    fail(error);
  }
}
